import { useState } from "react";
import { ShoppingBag, Zap } from "lucide-react";
import { toast } from "sonner";
import { findVariant, initialSelection, VariantPicker } from "./VariantPicker";
import { ProductDetails } from "./ProductDetails";
import { SizeGuide } from "@/components/site/SizeGuide";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/site";

const Description = ({ product }) =>
  product.description_html ? (
    <div className="product-prose" data-testid="product-description" dangerouslySetInnerHTML={{ __html: product.description_html }} />
  ) : (
    <p className="text-base leading-relaxed text-bone/75" data-testid="product-description">{product.description}</p>
  );

export const BuyBox = ({ product }) => {
  const { add, setOpen } = useCart();
  const [selection, setSelection] = useState(() => initialSelection(product));
  const variant = findVariant(product, selection);

  const addToCart = (openCart) => {
    if (!variant) {
      const missing = product.options.filter((o) => !selection[o.name]).map((o) => o.name.toLowerCase());
      toast.error(`Scegli ${missing.join(" e ") || "una variante"}`, { description: product.name });
      return;
    }
    add(product, variant);
    if (openCart) setOpen(true);
    else toast.success("Aggiunto al carrello", { description: `${product.name} — ${variant.title}` });
  };

  return (
    <div data-testid="product-buy-box">
      <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em]">
        <span className="border border-cream/40 px-2 py-1 text-cream">{product.badge}</span>
        <span className="text-smoke">{product.edition}</span>
        {product.demo && <span data-testid="product-demo-chip" className="bg-amber-300 px-2 py-1 text-ink">Dati di esempio</span>}
      </div>
      <h1 className="mt-6 font-display text-4xl font-black uppercase leading-[0.92] tracking-tight sm:text-5xl lg:text-6xl" data-testid="product-name">{product.name}</h1>
      <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.22em] text-cream/70">{product.tagline}</p>
      <p className="mt-6 font-mono text-3xl text-cream" data-testid="product-price">{formatPrice(product.price)}</p>
      <div className="mt-8"><Description product={product} /></div>
      <div className="mt-10 border-t border-line pt-8">
        <VariantPicker product={product} selection={selection} onChange={setSelection} />
        {product.size_guide && <div className="mt-4"><SizeGuide product={product} /></div>}
      </div>
      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <button onClick={() => addToCart(false)} data-testid="product-add-to-cart" className="inline-flex h-14 flex-1 items-center justify-center gap-3 border border-cream font-mono text-xs uppercase tracking-[0.2em] text-cream transition-[background-color,color,transform] duration-300 hover:bg-cream hover:text-ink active:scale-[0.98]">
          <ShoppingBag className="h-4 w-4" /> Aggiungi al carrello
        </button>
        <button onClick={() => addToCart(true)} data-testid="product-buy-now" className="inline-flex h-14 flex-1 items-center justify-center gap-3 bg-acid font-mono text-xs uppercase tracking-[0.2em] text-ink transition-transform duration-300 hover:-translate-y-0.5 active:scale-[0.98]">
          <Zap className="h-4 w-4" /> Acquista ora
        </button>
      </div>
      <div className="mt-10"><ProductDetails product={product} /></div>
    </div>
  );
};
